<template>
  <ToastRoot
    :duration="getToastDuration(toast)"
    class="overflow-y-auto rounded-lg border border-border bg-card px-4 py-3 shadow-lg transition hover:bg-muted hover:shadow-xl"
    :class="$style.toast"
    @update:open="removeToast(toast.id)"
  >
    <div>
      <div class="flex items-start">
        <div class="mr-3 grid h-6 shrink-0 place-items-center empty:hidden">
          <ExclamationTriangleIcon
            v-if="toast.type === 'warn'"
            class="size-4 text-amber-600"
          />
          <ExclamationCircleIcon
            v-else-if="toast.type === 'error'"
            class="size-4 text-red-600"
          />
          <CheckCircleIcon
            v-else-if="toast.type === 'success'"
            class="size-4 text-green-600"
          />
          <ArrowPathIcon
            v-else-if="toast.type === 'pending'"
            class="size-4 animate-spin"
          />
        </div>

        <ToastTitle
          class="overflow-hidden break-words text-sm font-medium leading-6 dark:text-foreground"
        >
          {{ toast.title }}
        </ToastTitle>

        <div class="grow"></div>

        <ToastClose
          aria-label="Close this toast"
          class="-mr-1 ml-4 grid size-6 shrink-0 place-items-center rounded text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-800 dark:text-muted-foreground dark:hover:bg-card dark:hover:text-white"
        >
          <XMarkIcon class="size-4" />
        </ToastClose>
      </div>
      <ToastDescription
        v-if="toast.message"
        class="mt-1 break-words text-[13px] leading-5 text-muted-foreground"
      >
        {{ toast.message }}
      </ToastDescription>
    </div>
  </ToastRoot>
</template>

<script lang="ts" setup>
import { ToastClose, ToastDescription, ToastRoot, ToastTitle } from "reka-ui";

import ArrowPathIcon from "@/icons/heroicons/arrow-path-micro.svg";
import CheckCircleIcon from "@/icons/heroicons/check-circle-micro.svg";
import ExclamationCircleIcon from "@/icons/heroicons/exclamation-circle-micro.svg";
import ExclamationTriangleIcon from "@/icons/heroicons/exclamation-triangle-micro.svg";
import XMarkIcon from "@/icons/heroicons/x-mark-micro.svg";
import { type InternalToast, UNSTABLE_toasts as toasts } from "@/lib/toasts";

const getToastDuration = (toast: InternalToast): number | undefined => {
  if (window.location.pathname.includes("vue-storybook")) {
    return undefined;
  }

  switch (toast.type) {
    case "inform":
    case "success":
      return 3000;
  }

  return 5_000;
};

const removeToast = (id: string) => {
  // Ugly hack because we want toasts to transition on close,
  // set this to be longer than the duration configured in the CSS below.
  setTimeout(() => {
    toasts.value = toasts.value.filter((toast) => toast.id !== id);
  }, 150);
};

defineProps<{
  toast: InternalToast;
}>();
</script>

<style module>
.toast {
  pointer-events: auto;

  @media (prefers-reduced-motion: no-preference) {
    &[data-state="open"] {
      animation: slide-in 150ms var(--motion-timing-ease);
    }
    &[data-state="closed"] :global {
      animation: fade-out 100ms ease-in;
    }
    &[data-swipe="move"] {
      transform: translateX(var(--reka-toast-swipe-move-x));
    }
    &[data-swipe="cancel"] {
      transform: translateX(0);
      transition: transform 200ms ease-out;
    }
    &[data-swipe="end"] {
      animation: swipe-out 100ms ease-out;
    }
  }
}

@keyframes slide-in {
  from {
    transform: translateX(calc(100% + var(--viewport-padding)));
  }
  to {
    transform: translateX(0);
  }
}

@keyframes swipe-out {
  from {
    transform: translateX(var(--reka-toast-swipe-end-x));
  }
  to {
    transform: translateX(calc(100% + var(--viewport-padding)));
  }
}
</style>
